import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import TableToolbar, { SortableTh } from "@/components/TableToolbar";
import { useTableView } from "@/hooks/useTableView";
import { useAuth } from "@/context/AuthContext";

export default function Overtime() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "",
    date: new Date().toISOString().slice(0, 10),
    hours: "",
    hourly_rate: "",
    currency: "TRY",
    note: "",
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data,
  });
  const { data: entries = [] } = useQuery({
    queryKey: ["overtime"], queryFn: async () => (await api.get("/overtime")).data,
  });

  const create = useMutation({
    mutationFn: async (p) => (await api.post("/overtime", p)).data,
    onSuccess: () => {
      qc.invalidateQueries(); setOpen(false); toast.success("Mesai kaydedildi");
      setForm({ employee_id: "", date: new Date().toISOString().slice(0, 10), hours: "", hourly_rate: "", currency: "TRY", note: "" });
    },
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/overtime/${id}`)).data,
    onSuccess: () => qc.invalidateQueries(),
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      hours: parseFloat(form.hours),
      hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
    };
    if (!payload.hourly_rate) delete payload.hourly_rate;
    create.mutate(payload);
  };

  const empName = (id) => employees.find((e) => e.id === id)?.name || "-";

  const view = useTableView({ categoryField: "employee_name", descField: "note", amountField: "total" });
  const enriched = entries.map((e) => ({ ...e, employee_name: empName(e.employee_id) }));
  const visible = view.apply(enriched);
  const totalHours = visible.reduce((s, e) => s + (e.hours || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="overtime-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Personel Ek Mesai</div>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Ek Mesai Kayıtları</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-md border border-border bg-white px-4 py-2">
            <div className="overline">Görüntülenen Saat</div>
            <div className="mono text-xl font-semibold">{totalHours.toFixed(1)} sa</div>
          </div>
          {isAdmin && <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="ot-add-btn"><Plus size={16} className="mr-1" /> Yeni Mesai</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Yeni Ek Mesai Kaydı</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Çalışan</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger data-testid="ot-employee"><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tarih</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div><Label>Saat</Label><Input type="number" step="0.25" required value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="ot-hours" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Saatlik Ücret (Ops.)</Label>
                    <Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="Boş: çalışan varsayılanı" />
                  </div>
                  <div>
                    <Label>Para Birimi</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Not</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
                <DialogFooter><Button type="submit" data-testid="ot-save" disabled={!form.employee_id}>Kaydet</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      <TableToolbar
        filters={view.filters}
        setFilters={view.setFilters}
        onReset={view.resetFilters}
        testidPrefix="ot"
        countInfo={`${visible.length} / ${entries.length} kayıt`}
      />

      <div className="mt-4 overflow-x-auto rounded-md border border-border bg-white">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b border-border bg-secondary/50">
            <tr className="text-xs uppercase text-muted-foreground tracking-wider">
              <SortableTh label="Tarih" sortKey="date" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid="ot-sort-date" />
              <SortableTh label="Çalışan" sortKey="employee_name" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid="ot-sort-employee" />
              <SortableTh label="Saat" sortKey="hours" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} align="right" testid="ot-sort-hours" />
              <SortableTh label="Saatlik" sortKey="hourly_rate" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} align="right" testid="ot-sort-rate" />
              <SortableTh label="Toplam" sortKey="total" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} align="right" testid="ot-sort-total" />
              <SortableTh label="Not" sortKey="note" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid="ot-sort-note" />
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((e) => (
              <tr key={e.id} className="border-b border-border/60 hover:bg-secondary/30">
                <td className="px-4 py-3 mono">{fmtDate(e.date)}</td>
                <td className="px-4 py-3">{e.employee_name}</td>
                <td className="px-4 py-3 mono text-right">{e.hours}</td>
                <td className="px-4 py-3 mono text-right">{fmtMoney(e.hourly_rate || 0, e.currency)}</td>
                <td className="px-4 py-3 mono text-right font-semibold">{fmtMoney(e.total || 0, e.currency)}</td>
                <td className="px-4 py-3 text-muted-foreground">{e.note || "-"}</td>
                <td className="px-4 py-3 text-right">
                  {isAdmin && <button onClick={() => remove.mutate(e.id)} data-testid={`del-ot-${e.id}`} className="text-destructive hover:opacity-70"><Trash size={14} /></button>}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                {entries.length === 0 ? "Mesai kaydı yok." : "Filtrelere uyan kayıt yok."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
